import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FieldError, useFormContext } from '../../core';

import { AlertMessage, Button, Icon, RegularModal } from '@/ui-kit';

import { cn } from '@/libs';
import { uiConfig } from '@/config';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  removeStateStagedFieldsGroup,
  setStateStagedFieldsGroup,
} from '@/store/thunks';
import { useGroupActions } from '@/forms/hooks';

import {
  adaptFormFieldValuesToSectionFormFieldUpdateRequest,
  adaptStagedFieldsGroupFormFieldToArrayFormField,
} from '@/adapters';

import { ArrayContainer, FormFieldMapper } from '..';
import { Checkbox } from '../inputs';
import { GroupRestrictionErrors } from './GroupRestrictionErrors';
import { getFieldIdWithoutIndex } from '../../utils';
import type {
  ArrayFormField,
  FormInitialValues,
  StagedFieldsGroupFormField,
} from '../../typings';
import {
  removeAddedStagedFieldsGroup,
  selectForm,
  setAddedStagedFieldsGroup,
} from '../../store';
import { FormContext } from '../../contexts';

import './StagedFieldsGroupPresenter.scss';
import {
  useClearOnNotApplicable,
  useGroupDeleteGuard,
  useMultipleField,
  useShouldValidateField,
} from '@/forms/hooks';
import { config } from '../../config';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';



type StagedFieldsGroupPresenterProps = {
  field: StagedFieldsGroupFormField;
  disabled?: boolean;
};

const containerClasses = cn(
  'sfg__container',
  uiConfig.grid.maxColumnsSpanClass,
);

const childrenClasses = cn(
  'grid',
  'gap-x-[20px]',
  'gap-y-[35px]',
  'grid-cols-1',
);

export const StagedFieldsGroupPresenter: React.FC<
  StagedFieldsGroupPresenterProps
> = ({ field, disabled }) => {
  const { formFieldGroups: formData } = useContext(FormContext);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [firstStageFieldNames, setFirstStageFieldNames] = useState<string[]>(
    [],
  );
  const [firstStageFields, setFirstStageFields] = useState<FormInitialValues>(
    {},
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useAppDispatch();
  const { addedStagedFieldsGroup } = useAppSelector(selectForm);
  const {
    addGroup,
    removeGroup,
    clearGroup,
    updateStagedGroup,
    refreshForm,
  } = useGroupActions();

  const { values, validateForm, setFieldTouched } =
    useFormContext<FormInitialValues>();

  const [fieldsArray, setFieldsArray] = useState<ArrayFormField | undefined>();

  const { requiredFieldId, isFieldNA, isLastItem, hasAnyValue } =
    useMultipleField(field);

  useClearOnNotApplicable(field, isFieldNA);

  const { allowRemove, minRequiredHint } = useGroupDeleteGuard(field);

  // Full once the last slot is filled at the maximum; an empty or Not Applicable last slot still needs its controls.
  const full =
    field.maxItems != null &&
    field.hasAnyValue &&
    !field.notApplicable &&
    field.index + 1 >= field.maxItems;

  // First-stage rules run while the provider fills the modal, not only once the entry exists.
  useShouldValidateField(
    field.fieldId,
    !!hasAnyValue || isModalVisible,
    config.validateFirstStageFieldSuffix,
  );

  const onFirstStageContinue = async () => {
    // The open modal already turned the first-stage rules on, so the form's own values apply.
    const errors = await validateForm();

    const fieldsWithError = Object.keys(errors).filter(fieldWithError =>
      firstStageFieldNames.includes(fieldWithError),
    );

    if (fieldsWithError.length) {
      fieldsWithError.forEach(fieldWithError =>
        setFieldTouched(fieldWithError, true),
      );
      return;
    } else {
      await updateStagedGroup({
        groupID: field.fieldId,
        fieldsWithValues: Object.entries(firstStageFields).map(
          ([fieldId, value]) =>
            adaptFormFieldValuesToSectionFormFieldUpdateRequest(
              value,
              fieldId,
              formData,
            ),
        ),
      });

      // Always refresh after a staged-group update; reloadRequired is implicit here.
      refreshForm();

      dispatch(
        removeAddedStagedFieldsGroup(getFieldIdWithoutIndex(field.fieldId)),
      );
      setIsCollapsed(false);
      setIsModalVisible(false);
    }
  };

  const removeItemHandler = useCallback(async () => {
    if (!fieldsArray) return;

    if (field.index === 0 && isLastItem) {
      clearGroup(field.fieldId);
    } else {
      dispatch(removeStateStagedFieldsGroup(field.fieldId));
      await removeGroup(fieldsArray.fieldId);
    }
  }, [
    dispatch,
    field.fieldId,
    field.index,
    fieldsArray,
    isLastItem,
    clearGroup,
    removeGroup,
  ]);

  const addItemHandler = useCallback(async () => {
    if (field.hasAnyValue) {
      // TODO: Validate that the field was created
      await addGroup(field.fieldId);
      dispatch(
        setAddedStagedFieldsGroup({
          group: getFieldIdWithoutIndex(field.fieldId),
          index: field.index + 1,
        }),
      );
    } else {
      setIsModalVisible(true);
    }
  }, [
    dispatch,
    field.fieldId,
    field.hasAnyValue,
    field.index,
    addGroup,
  ]);

  useEffect(() => {
    if (Object.keys(addedStagedFieldsGroup).length > 0) {
      const fieldId = getFieldIdWithoutIndex(field.fieldId);
      if (addedStagedFieldsGroup[fieldId] === field.index) {
        setIsModalVisible(true);
      }
    }
  }, [addedStagedFieldsGroup, field]);

  useEffect(() => {
    setFirstStageFieldNames(field.firstStageFields.map(f => f.fieldId));
    setFieldsArray(adaptStagedFieldsGroupFormFieldToArrayFormField(field));
  }, [field]);

  useEffect(() => {
    const filteredValues: FormInitialValues = {};
    Object.entries(values).forEach(entry => {
      const [key, value] = entry;
      if (firstStageFieldNames.includes(key)) {
        filteredValues[key] = value;
      }
    });
    setFirstStageFields(filteredValues);
  }, [values, firstStageFieldNames]);

  useEffect(() => {
    dispatch(setStateStagedFieldsGroup(field.fieldId, firstStageFieldNames));
  }, [dispatch, firstStageFieldNames, field]);

  return (
    // data-jump-scroll-only: jumpToField's first focusable descendant here is
    // ArrayContainer/GroupTitle's delete button, then its collapse toggle —
    // never a field, so a failed submit must not auto-focus either.
    <div
      id={field.fieldId}
      className={containerClasses}
      data-jump-scroll-only="true"
    >
      {isFieldNA && field.naDetails && (
        <FormFieldMapper field={field.naDetails} disabled={disabled} />
      )}

      {hasAnyValue && fieldsArray !== undefined ? (
        <ArrayContainer
          groupTitle={fieldsArray.title}
          groupSubtitle={fieldsArray.hint ? `(${fieldsArray.hint})` : undefined}
          isCollapsible
          isCollapsed={isCollapsed}
          allowRemove={allowRemove}
          minRequiredHint={minRequiredHint}
          disabled={isFieldNA || disabled}
          onCollapse={() => {
            setIsCollapsed(prev => !prev);
          }}
          onDelete={removeItemHandler}
          fieldsArray={fieldsArray}
          beforeChildren={
            field.alertMessage && (
              <FormFieldAlertMessage alertMessage={field.alertMessage} />
            )
          }
        />
      ) : (
        field.index === 0 &&
        isLastItem && (
          <>
            {field.alertMessage && (
              <FormFieldAlertMessage alertMessage={field.alertMessage} />
            )}
            <AlertMessage
              className="sfg__alert-message"
              variant="neutral"
              title={`There are no ${field.title}${
                field.title.endsWith('s') ? 'es' : 's'
              } added yet.`}
            />
          </>
        )
      )}

      {isLastItem && !full && (
        <div className="sfg__button">
          <Button
            type="button"
            leadingIcon={<Icon type="plus" />}
            onClick={addItemHandler}
            disabled={isFieldNA || disabled}
          >
            Add {field.title}
          </Button>
          {field.notApplicableField !== undefined && (
            <Checkbox
              name={field.notApplicableField.fieldId}
              label={field.notApplicableField.label}
            />
          )}
          {fieldsArray && (
            <RegularModal
              size="medium"
              onClose={() => {
                if (field.index !== 0) {
                  dispatch(removeStateStagedFieldsGroup(field.fieldId));
                  removeGroup(fieldsArray.fieldId);
                }
                setIsModalVisible(false);
              }}
              isOpen={isModalVisible}
              id={`${field.fieldId}-first-stage-modal`}
              title={`Add ${field.title}`}
              buttons={{
                variant: 'single',
                list: [
                  <Button onClick={onFirstStageContinue}>
                    Generate {field.title}
                  </Button>,
                ],
              }}
            >
              <div className={childrenClasses}>
                {field.firstStageFields.map(f => (
                  <FormFieldMapper key={f.fieldId} field={f} />
                ))}
              </div>
            </RegularModal>
          )}
        </div>
      )}

      <FieldError name={requiredFieldId} />

      <GroupRestrictionErrors
        groupFieldId={field.fieldId}
        restriction={field.restriction}
        fields={[...field.firstStageFields, ...field.otherFields]}
      />
    </div>
  );
};
